import React, { useState, useEffect } from 'react';
import { useProjectMerge } from '../../hooks/useProjectMerge';
import { Button } from '../ui/Button/Button';
import type { MergedTargets, MergeAction } from '../../types/merge';
import type { ReviewData } from '../../types/evaluation';

interface TargetsMergeProps {
  projectIds: [string, string];
  onComplete: (data: MergedTargets) => void;
  onBack: () => void;
  initialData?: MergedTargets;
}


export const TargetsMerge: React.FC<TargetsMergeProps> = ({
  projectIds,
  onComplete,
  onBack,
  initialData
}) => {
  const { mergeTargets, isLoading, error } = useProjectMerge();
  const [mergeData, setMergeData] = useState<MergedTargets | null>(initialData || null);
  const [duplicateActions, setDuplicateActions] = useState<{[key: string]: MergeAction}>({});

  useEffect(() => {
    if (!initialData) {
      loadMergeData();
    } else {
      const actions: {[key: string]: MergeAction} = {};
      initialData.duplicates.forEach((dup, index) => {
        actions[index.toString()] = dup.action;
      });
      setDuplicateActions(actions);
    }
  }, [projectIds, initialData]);

  const loadMergeData = async () => {
    const result = await mergeTargets(projectIds);
    if (result) {
      setMergeData(result);
      const actions: {[key: string]: MergeAction} = {};
      result.duplicates.forEach((dup, index) => {
        actions[index.toString()] = dup.action;
      });
      setDuplicateActions(actions);
    }
  };

  const handleDuplicateAction = (duplicateIndex: number, action: MergeAction) => {
    setDuplicateActions(prev => ({
      ...prev,
      [duplicateIndex.toString()]: action
    }));
  };

  const mergeProductDetails = (product1: ReviewData, product2: ReviewData): ReviewData => {
    // プロジェクト1のタイトルを使用し、詳細とコメントは連結
    return {
      reviewTitle: product1.reviewTitle,
      reviewComment: `【プロジェクト1】\n${product1.reviewComment || ''}${
        product2.reviewComment ? `\n\n【プロジェクト2】\n${product2.reviewComment}` : ''
      }`,
      reviewLink: product1.reviewLink || product2.reviewLink
    };
  };

  const handleComplete = () => {
    if (!mergeData) return;

    const updatedDuplicates = mergeData.duplicates.map((dup, index) => {
      const action = duplicateActions[index.toString()] ?? dup.action;
      const result = { ...dup, action };
      
      // マージを選択した場合、統合データを作成
      if (action === 'merge') {
        const product1 = mergeData.originalProjects[projectIds[0]][dup.indices[0]];
        const product2 = mergeData.originalProjects[projectIds[1]][dup.indices[1]];
        result.mergedData = mergeProductDetails(product1, product2);
      }
      
      return result;
    });

    // 最終的なマージ結果を作成
    const project1Products = mergeData.originalProjects[projectIds[0]];
    const project2Products = mergeData.originalProjects[projectIds[1]];
    const finalMerged: ReviewData[] = [];
    
    // プロジェクト1の製品を処理
    project1Products.forEach((product, index) => {
      const duplicate = updatedDuplicates.find(dup => dup.indices[0] === index);
      
      if (!duplicate) {
        // 重複なし
        finalMerged.push(product);
      } else {
        // 重複あり
        switch (duplicate.action) {
          case 'keep_first':
            finalMerged.push(product);
            break;
          case 'keep_both':
            finalMerged.push(product);
            break;
          case 'merge':
            if (duplicate.mergedData) {
              finalMerged.push(duplicate.mergedData);
            }
            break;
          // keep_secondの場合は何もしない
        }
      }
    });
    
    // プロジェクト2の製品を処理
    project2Products.forEach((product, index) => {
      const duplicate = updatedDuplicates.find(dup => dup.indices[1] === index);
      
      if (!duplicate) {
        // 重複なし
        finalMerged.push(product);
      } else {
        // 重複あり
        if (duplicate.action === 'keep_second' || duplicate.action === 'keep_both') {
          finalMerged.push(product);
        }
        // keep_first, mergeの場合は既に処理済み
      }
    });

    const finalData: MergedTargets = {
      ...mergeData,
      duplicates: updatedDuplicates,
      merged: finalMerged,
      userEdited: true
    };

    setMergeData(finalData);
    onComplete(finalData);
  };

  const getProjectName = (projectId: string): string => {
    const index = projectIds.indexOf(projectId);
    return index === 0 ? 'プロジェクト 1' : 'プロジェクト 2';
  };

  if (isLoading) {
    return (
      <div className="targets-merge">
        <div className="loading">
          <div className="loading-spinner" />
          <p>評価対象を統合中...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="targets-merge">
        <div className="error">
          <p>{error}</p>
          <Button onClick={loadMergeData}>再試行</Button>
        </div>
      </div>
    );
  }

  if (!mergeData) {
    return null;
  }

  const project1Products = mergeData.originalProjects[projectIds[0]];
  const project2Products = mergeData.originalProjects[projectIds[1]];

  return (
    <div className="targets-merge">
      <div className="merge-header">
        <h2>評価対象の統合</h2>
        <p>システムが分析した統合案を確認し、手動で調整してください</p>
        <div className="instructions">
          <h4>💡 操作方法</h4>
          <ul>
            <li>重複候補が検出された場合、どちらを残すか選択してください</li>
            <li>最終的な統合結果をプレビューで確認できます</li>
            <li>問題なければ「次のステップへ」で観点の統合に進みます</li>
          </ul>
        </div>
      </div>

      <div className="merge-summary">
        <div className="summary-stats">
          <div className="stat">
            <span className="stat-label">{getProjectName(projectIds[0])}</span>
            <span className="stat-value">{project1Products.length}件</span>
          </div>
          <span className="stat-plus">+</span>
          <div className="stat">
            <span className="stat-label">{getProjectName(projectIds[1])}</span>
            <span className="stat-value">{project2Products.length}件</span>
          </div>
          <span className="stat-arrow">→</span>
          <div className="stat">
            <span className="stat-label">統合後</span>
            <span className="stat-value">{mergeData.merged.length}件</span>
          </div>
        </div>
      </div>

      {mergeData.duplicates.length > 0 && (
        <div className="duplicates-section">
          <h3>🔍 重複検出の結果（システム提案）</h3>
          <p>類似した評価対象が検出されました。統合方法を手動で選択してください。</p>
          
          <div className="duplicates-list">
            {mergeData.duplicates.map((duplicate, index) => {
              const product1 = project1Products[duplicate.indices[0]];
              const product2 = project2Products[duplicate.indices[1]];
              const currentAction = duplicateActions[index.toString()] ?? duplicate.action;
              
              return (
                <div key={index} className="duplicate-item">
                  <div className="duplicate-header">
                    <h4>重複 {index + 1}</h4>
                    <span className="similarity-score">
                      類似度: {(duplicate.similarity * 100).toFixed(0)}%
                    </span>
                  </div>
                  
                  <div className="action-selector">
                    <label>統合方法:</label>
                    <div className="action-buttons">
                      <button
                        className={`action-btn ${currentAction === 'keep_first' ? 'selected' : ''}`}
                        onClick={() => handleDuplicateAction(index, 'keep_first')}
                      >
                        プロジェクト1のみ
                      </button>
                      <button
                        className={`action-btn ${currentAction === 'keep_second' ? 'selected' : ''}`}
                        onClick={() => handleDuplicateAction(index, 'keep_second')}
                      >
                        プロジェクト2のみ
                      </button>
                      <button
                        className={`action-btn ${currentAction === 'keep_both' ? 'selected' : ''}`}
                        onClick={() => handleDuplicateAction(index, 'keep_both')}
                      >
                        両方別々で残す
                      </button>
                      <button
                        className={`action-btn ${currentAction === 'merge' ? 'selected' : ''}`}
                        onClick={() => handleDuplicateAction(index, 'merge')}
                      >
                        詳細を統合
                      </button>
                    </div>
                  </div>
                  
                  <div className="duplicate-options">
                    <div className={`option ${currentAction === 'keep_first' || currentAction === 'keep_both' || currentAction === 'merge' ? 'included' : ''}`}>
                      <div className="option-header">
                        <span className="option-source">{getProjectName(projectIds[0])}</span>
                        {(currentAction === 'keep_first' || currentAction === 'keep_both') && <span className="selected-badge">残す</span>}
                        {currentAction === 'merge' && <span className="selected-badge merge">統合元</span>}
                      </div>
                      <h5>{product1.reviewTitle}</h5>
                      <p>{product1.reviewComment}</p>
                    </div>
                    
                    <div className={`option ${currentAction === 'keep_second' || currentAction === 'keep_both' || currentAction === 'merge' ? 'included' : ''}`}>
                      <div className="option-header">
                        <span className="option-source">{getProjectName(projectIds[1])}</span>
                        {(currentAction === 'keep_second' || currentAction === 'keep_both') && <span className="selected-badge">残す</span>}
                        {currentAction === 'merge' && <span className="selected-badge merge">統合元</span>}
                      </div>
                      <h5>{product2.reviewTitle}</h5>
                      <p>{product2.reviewComment}</p>
                    </div>
                  </div>
                  
                  {currentAction === 'merge' && (
                    <div className="merge-preview">
                      <h5>統合結果プレビュー:</h5>
                      <div className="merged-product">
                        <strong>{product1.reviewTitle}</strong>
                        <pre>{mergeProductDetails(product1, product2).reviewComment}</pre>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="merged-preview">
        <h3>📋 統合結果プレビュー（最終確認）</h3>
        <p>以下が統合後の評価対象リストです。問題があれば上記で調整してください。</p>
        <div className="targets-grid">
          {mergeData.merged.map((product, index) => (
            <div key={index} className="target-card">
              <h4>{product.reviewTitle}</h4>
              <p>{product.reviewComment}</p>
              {product.reviewLink && (
                <a href={product.reviewLink} target="_blank" rel="noopener noreferrer">
                  リンクを表示
                </a>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="merge-actions">
        <Button variant="ghost" onClick={onBack}>
          戻る
        </Button>
        <Button variant="primary" onClick={handleComplete}>
          次のステップへ
        </Button>
      </div>

      <style>{`
        .targets-merge {
          padding: 1rem;
        }

        .merge-header {
          text-align: center;
          margin-bottom: 2rem;
        }

        .merge-header h2 {
          font-size: 1.5rem;
          color: #1f2937;
          margin: 0 0 0.5rem 0;
        }

        .merge-header p {
          color: #6b7280;
          margin: 0 0 1rem 0;
        }

        .instructions {
          background: #f0f9ff;
          border: 1px solid #bae6fd;
          border-radius: 0.5rem;
          padding: 1rem;
          margin-top: 1rem;
        }

        .instructions h4 {
          color: #0c4a6e;
          margin: 0 0 0.5rem 0;
          font-size: 1rem;
        }

        .instructions ul {
          color: #075985;
          margin: 0;
          padding-left: 1.5rem;
        }

        .instructions li {
          margin-bottom: 0.25rem;
        }

        .loading, .error {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 4rem 2rem;
          text-align: center;
        }

        .loading-spinner {
          width: 2rem;
          height: 2rem;
          border: 2px solid #e5e7eb;
          border-top: 2px solid #3b82f6;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin-bottom: 1rem;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        .merge-summary {
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          border-radius: 0.5rem;
          padding: 1.5rem;
          margin-bottom: 2rem;
        }

        .summary-stats {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 1rem;
        }

        .stat {
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
        }

        .stat-label {
          font-size: 0.875rem;
          color: #6b7280;
          margin-bottom: 0.25rem;
        }

        .stat-value {
          font-size: 1.5rem;
          font-weight: bold;
          color: #1f2937;
        }

        .stat-plus, .stat-arrow {
          font-size: 1.25rem;
          color: #3b82f6;
          font-weight: bold;
        }

        .duplicates-section {
          margin-bottom: 2rem;
        }

        .duplicates-section h3 {
          font-size: 1.25rem;
          color: #1f2937;
          margin: 0 0 0.5rem 0;
        }

        .duplicates-section p {
          color: #6b7280;
          margin: 0 0 1rem 0;
        }

        .duplicates-list {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .duplicate-item {
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 0.5rem;
          padding: 1.5rem;
        }

        .duplicate-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1rem;
        }

        .duplicate-header h4 {
          font-size: 1rem;
          color: #1f2937;
          margin: 0;
        }

        .similarity-score {
          font-size: 0.875rem;
          color: #3b82f6;
          background: #dbeafe;
          padding: 0.25rem 0.5rem;
          border-radius: 0.25rem;
        }

        .action-selector {
          margin: 1rem 0;
          padding: 1rem;
          background: #f9fafb;
          border-radius: 0.5rem;
        }

        .action-selector label {
          display: block;
          font-size: 0.875rem;
          color: #4b5563;
          margin-bottom: 0.5rem;
          font-weight: 500;
        }

        .action-buttons {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 0.5rem;
        }

        .action-btn {
          padding: 0.5rem 0.75rem;
          border: 1px solid #d1d5db;
          background: white;
          border-radius: 0.375rem;
          font-size: 0.875rem;
          cursor: pointer;
          transition: all 0.2s;
        }

        .action-btn:hover {
          border-color: #3b82f6;
          background: #eff6ff;
        }

        .action-btn.selected {
          background: #3b82f6;
          color: white;
          border-color: #3b82f6;
        }

        .duplicate-options {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 1rem;
          margin-top: 1rem;
        }

        .option {
          background: #f9fafb;
          border: 2px solid #e5e7eb;
          border-radius: 0.5rem;
          padding: 1rem;
          transition: all 0.2s;
          opacity: 0.5;
        }

        .option.included {
          opacity: 1;
          background: white;
        }

        .option.included .option-header {
          border-bottom: 1px solid #e5e7eb;
          padding-bottom: 0.5rem;
          margin-bottom: 0.75rem;
        }

        .option-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 0.5rem;
        }

        .option-source {
          font-size: 0.75rem;
          color: #6b7280;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }

        .selected-badge {
          font-size: 0.75rem;
          color: #10b981;
          background: #dcfce7;
          padding: 0.125rem 0.375rem;
          border-radius: 0.25rem;
        }

        .selected-badge.merge {
          color: #7c3aed;
          background: #ede9fe;
        }

        .merge-preview {
          margin-top: 1rem;
          padding: 1rem;
          background: #f3f4f6;
          border-radius: 0.5rem;
          border: 1px solid #d1d5db;
        }

        .merge-preview h5 {
          font-size: 0.875rem;
          color: #4b5563;
          margin: 0 0 0.5rem 0;
        }

        .merged-product {
          background: white;
          padding: 0.75rem;
          border-radius: 0.375rem;
          border: 1px solid #e5e7eb;
        }

        .merged-product strong {
          display: block;
          margin-bottom: 0.5rem;
          color: #1f2937;
        }

        .merged-product pre {
          font-family: inherit;
          font-size: 0.875rem;
          color: #4b5563;
          white-space: pre-wrap;
          margin: 0;
        }

        .option h5 {
          font-size: 1rem;
          color: #1f2937;
          margin: 0 0 0.5rem 0;
        }

        .option p {
          color: #6b7280;
          font-size: 0.875rem;
          margin: 0 0 0.5rem 0;
          line-height: 1.4;
        }

        .option a {
          color: #3b82f6;
          text-decoration: none;
          font-size: 0.875rem;
        }

        .option a:hover {
          text-decoration: underline;
        }

        .merged-preview {
          margin-bottom: 2rem;
        }

        .merged-preview h3 {
          font-size: 1.25rem;
          color: #1f2937;
          margin: 0 0 1rem 0;
        }

        .targets-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
          gap: 1rem;
        }

        .target-card {
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 0.5rem;
          padding: 1rem;
        }

        .target-card h4 {
          font-size: 1rem;
          color: #1f2937;
          margin: 0 0 0.5rem 0;
        }

        .target-card p {
          color: #6b7280;
          font-size: 0.875rem;
          margin: 0 0 0.5rem 0;
          line-height: 1.4;
        }

        .target-card a {
          color: #3b82f6;
          text-decoration: none;
          font-size: 0.875rem;
        }

        .target-card a:hover {
          text-decoration: underline;
        }

        .merge-actions {
          display: flex;
          justify-content: space-between;
          gap: 1rem;
          margin-top: 2rem;
        }

        @media (max-width: 768px) {
          .summary-stats {
            flex-direction: column;
            gap: 0.5rem;
          }

          .stat-plus, .stat-arrow {
            transform: rotate(90deg);
          }

          .action-buttons {
            grid-template-columns: repeat(2, 1fr);
          }

          .duplicate-options {
            grid-template-columns: 1fr;
          }

          .targets-grid {
            grid-template-columns: 1fr;
          }

          .merge-actions {
            flex-direction: column-reverse;
          }
        }
      `}</style>
    </div>
  );
};